import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  TextField,
  Button,
  ButtonGroup,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  useTheme,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ViewList as ViewListIcon,
  ViewColumn as ViewColumnIcon,
  DateRange as DateRangeIcon,
  Assessment as AssessmentIcon,
  LocalHospital as LocalHospitalIcon,
  Science as ScienceIcon,
  MonitorHeart as MonitorHeartIcon,
  Bloodtype as BloodtypeIcon,
  Biotech as BiotechIcon,
  Psychology as PsychologyIcon,
  FilterList as FilterListIcon,
  GetApp as ExportIcon,
  Description as NotesIcon,
  Assignment as SummaryIcon,
  Medication as MedicationIcon,
  ShowChart as FlowsheetIcon,
  Assignment as CarePlanIcon,
  LocalHospital as RadiologyIcon,
  MedicalServices as OperativeIcon,
  Group as RoundingIcon,
  Warning as AllergyIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, subDays, subMonths, startOfDay, endOfDay, isValid, parseISO } from 'date-fns';
import { ReportConfigService } from '../../services/reportConfigService';
import { getTestDefinitions, getTestPanels, getLabResults } from '../../services/labReportsService';
import { getRadiologyReports } from '../../services/radiologyReportsService';
import ReportDetailsModal from '../patients/ReportDetailsModal';

// Safe date formatting helper
const safeFormatDate = (dateValue, formatString = 'MM/dd/yyyy') => {
  if (!dateValue) return null;
  try {
    let date;
    if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === 'string') {
      date = parseISO(dateValue);
    } else if (dateValue?.seconds) {
      // Firestore Timestamp
      date = new Date(dateValue.seconds * 1000);
    } else if (dateValue?.toDate) {
      date = dateValue.toDate();
    } else {
      date = new Date(dateValue);
    }
    return isValid(date) ? format(date, formatString) : null;
  } catch {
    return null;
  }
};

// Category icons mapping for different test categories
const CATEGORY_ICONS = {
  'Hematology': <BloodtypeIcon />,
  'CBC': <BloodtypeIcon />,
  'Coagulation': <BiotechIcon />,
  'Chemistry': <ScienceIcon />,
  'Liver': <LocalHospitalIcon />,
  'Liver Function': <LocalHospitalIcon />,
  'Cardiac': <MonitorHeartIcon />,
  'Cardiology': <MonitorHeartIcon />,
  'Lipid': <AssessmentIcon />,
  'Lipids': <AssessmentIcon />,
  'Thyroid': <PsychologyIcon />,
  'Endocrinology': <PsychologyIcon />,
  'Microbiology': <BiotechIcon />,
  'Immunology': <BiotechIcon />,
  'Toxicology': <ScienceIcon />,
  'Urinalysis': <ScienceIcon />,
  'Default': <ScienceIcon />
};

// Function to get icon for a category
const getCategoryIcon = (category) => {
  if (!category) return CATEGORY_ICONS['Default'];
  
  // Check for exact match first
  if (CATEGORY_ICONS[category]) {
    return CATEGORY_ICONS[category];
  }
  
  // Check for partial matches
  const categoryLower = category.toLowerCase();
  for (const [key, icon] of Object.entries(CATEGORY_ICONS)) {
    if (categoryLower.includes(key.toLowerCase()) || key.toLowerCase().includes(categoryLower)) {
      return icon;
    }
  }
  
  return CATEGORY_ICONS['Default'];
};

const VIEW_MODES = {
  TEST_BY_DATE: 'test_by_date', // Tests as rows, dates as columns
  DATE_BY_TEST: 'date_by_test'  // Dates as rows, tests as columns
};

// Report tab configurations
const REPORT_TAB_CONFIG = {
  'hp': {
    key: 'hp',
    label: 'H&P',
    icon: <AssessmentIcon />,
    description: 'History & Physical'
  },
  'notes': {
    key: 'notes',
    label: 'NOTES',
    icon: <NotesIcon />,
    description: 'Progress Notes & Consult Notes',
    consolidates: ['progress_notes', 'consult_notes']
  },
  'discharge_summary': {
    key: 'discharge_summary',
    label: 'DISCHARGE SUMMARY',
    icon: <SummaryIcon />,
    description: 'Discharge Summary'
  },
  'mar': {
    key: 'mar',
    label: 'MAR',
    icon: <MedicationIcon />,
    description: 'Medication Administration Record'
  },
  'flowsheets': {
    key: 'flowsheets',
    label: 'FLOWSHEETS',
    icon: <FlowsheetIcon />,
    description: 'Vitals, nursing assessments, intake/output'
  },
  'care_plans': {
    key: 'care_plans',
    label: 'CARE PLANS',
    icon: <CarePlanIcon />,
    description: 'Interdisciplinary care plans'
  },
  'lab_reports': {
    key: 'lab_reports',
    label: 'LAB',
    icon: <ScienceIcon />,
    description: 'Laboratory Results with trends'
  },
  'radiology_reports': {
    key: 'radiology_reports',
    label: 'RADIOLOGY',
    icon: <RadiologyIcon />,
    description: 'Imaging studies results'
  },
  'operative_reports': {
    key: 'operative_reports',
    label: 'OPERATIVE',
    icon: <OperativeIcon />,
    description: 'Surgical procedure reports'
  },
  'interdisciplinary_rounding': {
    key: 'interdisciplinary_rounding',
    label: 'ROUNDING',
    icon: <RoundingIcon />,
    description: 'Rounding report'
  },
  'allergies_problems': {
    key: 'allergies_problems',
    label: 'ALLERGIES',
    icon: <AllergyIcon />,
    description: 'Problem list and allergies'
  },
  'clinical_summary': {
    key: 'clinical_summary',
    label: 'SUMMARY',
    icon: <AssessmentIcon />,
    description: 'Aggregated clinical snapshot'
  }
};

const PersonalizedReporting = ({
  facilityId,
  patientId,
  encounterId = null,
  patient = null,
  encounter = null,
  showPatientHeader = true,
  onClose = null
}) => {
  const theme = useTheme();
  
  // State management
  const [reportConfigs, setReportConfigs] = useState([]);
  const [labResults, setLabResults] = useState([]);
  const [radiologyReports, setRadiologyReports] = useState([]);
  const [testCategories, setTestCategories] = useState({});
  const [testDefinitions, setTestDefinitions] = useState([]);
  const [testPanels, setTestPanels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('lab_reports'); // Default to Lab tab
  const [selectedCategory, setSelectedCategory] = useState('');
  const [viewMode, setViewMode] = useState(VIEW_MODES.TEST_BY_DATE);
  const [dateRange, setDateRange] = useState({
    startDate: subMonths(new Date(), 6), // Default to last 6 months
    endDate: new Date()
  });
  const [quickDateRange, setQuickDateRange] = useState('6months');
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [selectedTests, setSelectedTests] = useState({}); // { categoryId: [testName1, testName2, ...] }
  const [selectedRadiologyReport, setSelectedRadiologyReport] = useState(null);
  const [showRadiologyModal, setShowRadiologyModal] = useState(false);

  // Get available tabs based on enabled report configurations
  const availableTabs = useMemo(() => {
    const tabs = [];
    
    reportConfigs.forEach(config => {
      // Check if this report type should be consolidated into Notes tab
      if (['progress_notes', 'consult_notes'].includes(config.reportKey)) {
        // Add Notes tab if not already added
        if (!tabs.find(tab => tab.key === 'notes')) {
          tabs.push(REPORT_TAB_CONFIG.notes);
        }
      } else if (REPORT_TAB_CONFIG[config.reportKey]) {
        // Add individual report tab
        tabs.push(REPORT_TAB_CONFIG[config.reportKey]);
      }
    });
    
    // Sort tabs by key for consistent ordering
    return tabs.sort((a, b) => {
      const order = ['hp', 'notes', 'discharge_summary', 'mar', 'flowsheets', 'care_plans', 'lab_reports', 'radiology_reports', 'operative_reports', 'interdisciplinary_rounding', 'allergies_problems', 'clinical_summary'];
      return order.indexOf(a.key) - order.indexOf(b.key);
    });
  }, [reportConfigs]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Load report configurations
  const loadReportConfigurations = useCallback(async () => {
    try {
      const configs = await ReportConfigService.getEnabledReports(facilityId, 'clinical');
      setReportConfigs(configs);
    } catch (err) {
      console.error('Error loading report configurations:', err);
      // Set default configs if service fails
      setReportConfigs([
        { reportKey: 'lab_reports', enabled: true },
        { reportKey: 'hp', enabled: true },
        { reportKey: 'notes', enabled: true },
        { reportKey: 'discharge_summary', enabled: true },
        { reportKey: 'mar', enabled: true },
        { reportKey: 'flowsheets', enabled: true },
        { reportKey: 'care_plans', enabled: true },
        { reportKey: 'radiology_reports', enabled: true },
        { reportKey: 'operative_reports', enabled: true },
        { reportKey: 'interdisciplinary_rounding', enabled: true },
        { reportKey: 'allergies_problems', enabled: true },
        { reportKey: 'clinical_summary', enabled: true }
      ]);
    }
  }, [facilityId]);

  // Load test categories from test definitions
  const loadTestCategories = useCallback(async () => {
    try {
      // Load test definitions from the service layer
      const testDefs = await getTestDefinitions({
        testType: 'Lab',
        active: true
      });
      setTestDefinitions(testDefs);

      // Group test definitions by category
      const categoriesMap = {};
      testDefs.forEach(testDef => {
        const category = testDef.category || 'Uncategorized';
        if (!categoriesMap[category]) {
          categoriesMap[category] = {
            name: category,
            tests: [],
            icon: getCategoryIcon(category)
          };
        }
        if (testDef.analytes && Array.isArray(testDef.analytes)) {
          categoriesMap[category].tests.push(...testDef.analytes);
        }
      });

      // Remove duplicates from test arrays
      Object.keys(categoriesMap).forEach(category => {
        categoriesMap[category].tests = [...new Set(categoriesMap[category].tests)];
      });

      setTestCategories(categoriesMap);
    } catch (err) {
      console.error('Error loading test categories:', err);
      // Set default categories if loading fails
      setTestCategories({
        'Hematology': {
          name: 'Hematology',
          tests: ['WBC', 'RBC', 'HGB', 'HCT', 'PLT', 'MCV', 'MCH', 'MCHC'],
          icon: getCategoryIcon('Hematology')
        },
        'Chemistry': {
          name: 'Chemistry',
          tests: ['GLU', 'BUN', 'CRE', 'NA', 'K', 'CL', 'CO2', 'ALB', 'TPROT'],
          icon: getCategoryIcon('Chemistry')
        },
        'Lipids': {
          name: 'Lipids',
          tests: ['CHOL', 'HDL', 'LDL', 'TRIG', 'NONHDL'],
          icon: getCategoryIcon('Lipids')
        },
        'Liver Function': {
          name: 'Liver Function',
          tests: ['ALT', 'AST', 'ALP', 'GGT', 'TBILI', 'DBILI'],
          icon: getCategoryIcon('Liver Function')
        },
        'Endocrinology': {
          name: 'Endocrinology',
          tests: ['TSH', 'FT4', 'FT3', 'T3', 'T4', 'HBA1C'],
          icon: getCategoryIcon('Endocrinology')
        },
        'Coagulation': {
          name: 'Coagulation',
          tests: ['PT', 'PTT', 'INR', 'FIB'],
          icon: getCategoryIcon('Coagulation')
        }
      });
    }
  }, []);

  // Load test panels
  const loadTestPanels = useCallback(async () => {
    try {
      // Load test panels from the service layer
      const panels = await getTestPanels({ active: true });
      setTestPanels(panels);
    } catch (err) {
      console.error('Error loading test panels:', err);
      // Set default panels if loading fails
      setTestPanels([
        {
          id: 'cbc-panel',
          name: 'Complete Blood Count (CBC)',
          description: 'Comprehensive blood cell count and morphology analysis',
          category: 'Hematology',
          analytes: ['WBC', 'RBC', 'HGB', 'HCT', 'PLT', 'MCV', 'MCH', 'MCHC'],
          active: true
        },
        {
          id: 'cmp-panel',
          name: 'Comprehensive Metabolic Panel (CMP)',
          description: 'Basic metabolic function and electrolyte balance',
          category: 'Chemistry',
          analytes: ['GLU', 'BUN', 'CRE', 'EGFR', 'NA', 'K', 'CL', 'CO2', 'TPROT', 'ALB', 'TBILI'],
          active: true
        },
        {
          id: 'lipid-panel',
          name: 'Lipid Panel',
          description: 'Cardiovascular risk assessment profile',
          category: 'Chemistry',
          analytes: ['CHOL', 'HDL', 'LDL', 'TRIG', 'NONHDL'],
          active: true
        }
      ]);
    }
  }, []);

  // Generate mock lab data for demo purposes
  const generateMockLabData = useCallback(() => {
    const mockResults = [];
    const testList = Object.values(testCategories).flatMap(cat => cat.tests);
    
    // If no test categories loaded, use a fallback list
    const finalTestList = testList.length > 0 ? testList : [
      'WBC', 'RBC', 'HGB', 'HCT', 'PLT', 'GLU', 'BUN', 'CRE', 'NA', 'K', 'CL', 'CO2', 'CHOL', 'HDL', 'LDL', 'TRIG'
    ];

    // Generate mock results for the last 6 months
    const now = new Date();
    for (let i = 0; i < 10; i++) { // Generate 10 sample dates
      const date = subDays(now, i * 18); // Every 18 days
      
      finalTestList.forEach(testName => {
        // Generate realistic mock values
        const mockResult = {
          id: `mock-${i}-${testName}`,
          procedure_result_id: `RES-${i}-${testName}`,
          procedure_report_id: `RPT-${i}`,
          test_code: testName,
          test_name: testName,
          result: generateMockResult(testName),
          units: getMockUnits(testName),
          ref_range: getMockRefRange(testName),
          abnormal: Math.random() > 0.8 ? (Math.random() > 0.5 ? 'H' : 'L') : 'N', // 20% chance of abnormal
          result_status: 'final',
          patientId: patientId,
          collection_date: date
        };
        mockResults.push(mockResult);
      });
    }
    
    setLabResults(mockResults);
  }, [testCategories, patientId]);

  // Helper functions for mock data generation
  const generateMockResult = (testName) => {
    const ranges = {
      'WBC': [4.0, 11.0],
      'RBC': [4.2, 5.4],
      'HGB': [12.0, 15.5],
      'HCT': [36.0, 46.0],
      'PLT': [150, 450],
      'GLU': [70, 140],
      'BUN': [7, 20],
      'CRE': [0.6, 1.5],
      'NA': [135, 145],
      'K': [3.5, 5.0],
      'CL': [96, 106],
      'CO2': [22, 30],
      'CHOL': [100, 200],
      'HDL': [40, 60],
      'LDL': [70, 130],
      'TRIG': [50, 150]
    };
    
    const range = ranges[testName] || [1, 10];
    const value = range[0] + Math.random() * (range[1] - range[0]);
    return value.toFixed(1);
  };

  const getMockUnits = (testName) => {
    const units = {
      'WBC': '10^3/μL',
      'RBC': '10^6/μL',
      'HGB': 'g/dL',
      'HCT': '%',
      'PLT': '10^3/μL',
      'GLU': 'mg/dL',
      'BUN': 'mg/dL',
      'CRE': 'mg/dL',
      'NA': 'mEq/L',
      'K': 'mEq/L',
      'CL': 'mEq/L',
      'CO2': 'mEq/L',
      'CHOL': 'mg/dL',
      'HDL': 'mg/dL',
      'LDL': 'mg/dL',
      'TRIG': 'mg/dL'
    };
    return units[testName] || '';
  };

  const getMockRefRange = (testName) => {
    const ranges = {
      'WBC': '4.0-11.0',
      'RBC': '4.2-5.4',
      'HGB': '12.0-15.5',
      'HCT': '36.0-46.0',
      'PLT': '150-450',
      'GLU': '70-140',
      'BUN': '7-20',
      'CRE': '0.6-1.5',
      'NA': '135-145',
      'K': '3.5-5.0',
      'CL': '96-106',
      'CO2': '22-30',
      'CHOL': '100-200',
      'HDL': '40-60',
      'LDL': '70-130',
      'TRIG': '50-150'
    };
    return ranges[testName] || '';
  };

  // Wrap loadLabResults in useCallback
  const loadLabResults = useCallback(async () => {
    try {
      // Use service layer to fetch lab results
      const results = await getLabResults([], {
        patientId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });
      setLabResults(results);
    } catch (err) {
      console.error('Error loading lab results:', err);
      if (err.code !== 'failed-precondition') {
        generateMockLabData();
      }
    }
  }, [patientId, dateRange, generateMockLabData]);

  // Generate mock radiology data
  const generateMockRadiologyData = useCallback(() => {
    const mockReports = [
      {
        id: 'mock_rad_1',
        testType: 'CT Head without Contrast',
        reportType: 'radiology',
        reportStatus: 'Final',
        review_status: 'final',
        provider_id: 'Dr. Demo Radiologist',
        comments: 'CT Head without Contrast',
        date_report: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 days ago
        date_collected: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        reportText: 'Findings: No acute intracranial abnormality. Impression: Normal CT Head without Contrast.',
        images: ['gs://mock-bucket/ct-head-1.dcm', 'gs://mock-bucket/ct-head-2.dcm', 'gs://mock-bucket/ct-head-3.dcm']
      },
      {
        id: 'mock_rad_2',
        testType: 'Chest X-Ray',
        reportType: 'radiology',
        reportStatus: 'Final',
        review_status: 'final',
        provider_id: 'Dr. Demo Radiologist',
        comments: 'Chest X-Ray, single view',
        date_report: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000), // 14 days ago
        date_collected: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000),
        reportText: 'Findings: Normal cardiac silhouette. Clear lung fields. No acute cardiopulmonary abnormality. Impression: Normal chest radiograph.',
        images: ['gs://mock-bucket/chest-xray-1.dcm']
      }
    ];
    setRadiologyReports(mockReports);
  }, []);

  // Wrap loadRadiologyReports in useCallback
  const loadRadiologyReports = useCallback(async () => {
    try {
      // Use service layer to fetch radiology reports
      const reports = await getRadiologyReports(patientId, {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        reportType: 'radiology'
      });

      // Set the reports directly (no need for duplicate call since both endpoints return the same data)
      setRadiologyReports(reports);
    } catch (err) {
      console.error('Error loading radiology reports:', err);
      // Generate mock radiology data if no real data found
      generateMockRadiologyData();
    }
  }, [patientId, dateRange, generateMockRadiologyData]);

  // Wrap loadData in useCallback
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const promises = [loadReportConfigurations(), loadTestCategories(), loadTestPanels()];
      await Promise.all(promises);
    } catch (err) {
      console.error('Error loading reporting data:', err);
      setError('Failed to load reporting data');
    } finally {
      setLoading(false);
    }
  }, [loadReportConfigurations, loadTestCategories, loadTestPanels]);

  // Main useEffect for loading data
  useEffect(() => {
    if (!facilityId || !patientId) {
      console.log('Missing required props:', { facilityId, patientId });
      setError('Missing required facility or patient information');
      setLoading(false);
      return;
    }

    console.log('Loading report data for:', { facilityId, patientId, encounterId });
    loadData();
  }, [facilityId, patientId, encounterId, loadData]);

  // Separate useEffect for lab results when date range changes or switching to lab tab
  useEffect(() => {
    if (!facilityId || !patientId || activeTab !== 'lab_reports') {
      return;
    }

    console.log('Loading lab results for:', { facilityId, patientId, dateRange });
    loadLabResults();
  }, [dateRange, facilityId, patientId, activeTab, loadLabResults]);

  // Separate useEffect for radiology reports when date range changes or switching to radiology tab
  useEffect(() => {
    if (!facilityId || !patientId || activeTab !== 'radiology_reports') {
      return;
    }

    console.log('Loading radiology reports for:', { facilityId, patientId, dateRange });
    loadRadiologyReports();
  }, [dateRange, facilityId, patientId, activeTab, loadRadiologyReports]);

  // Set default active tab when available tabs change
  useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.find(tab => tab.key === activeTab)) {
      // If current active tab is not available, set to first available tab
      setActiveTab(availableTabs[0].key);
    }
  }, [availableTabs, activeTab]);

  // Set default selected category when test categories are loaded
  // Note: We only set the selected category, but do NOT auto-expand it
  // Let users manually expand categories they want to see
  useEffect(() => {
    if (Object.keys(testCategories).length > 0 && !selectedCategory) {
      const firstCategory = Object.keys(testCategories)[0];
      setSelectedCategory(firstCategory);
      // Don't auto-expand - let user expand manually like other categories
    }
  }, [testCategories, selectedCategory]);

  // Initialize selected tests when test categories and panels are loaded (all tests selected by default)
  useEffect(() => {
    if (Object.keys(testCategories).length > 0 && Object.keys(selectedTests).length === 0) {
      const initialSelectedTests = {};
      
      // Initialize for regular categories
      Object.entries(testCategories).forEach(([categoryId, category]) => {
        initialSelectedTests[categoryId] = [...category.tests]; // Select all tests by default
      });
      
      // Initialize for test panels
      testPanels.forEach(panel => {
        const panelId = `panel_${panel.id}`;
        const panelTests = [];
        
        // Get test names from the panel's analytes or test definitions
        if (panel.analytes && Array.isArray(panel.analytes)) {
          // Direct analytes array (preferred method)
          panelTests.push(...panel.analytes);
        } else if (panel.tests && Array.isArray(panel.tests)) {
          // Test definitions references
          panel.tests.forEach(test => {
            // Handle both cases: test as object or test as string ID
            if (typeof test === 'string') {
              // Find the test definition and get its analytes
              const testDef = testDefinitions.find(def => def.id === test);
              if (testDef && testDef.analytes) {
                panelTests.push(...testDef.analytes);
              }
            } else if (test.analytes) {
              panelTests.push(...test.analytes);
            }
          });
        }
        
        initialSelectedTests[panelId] = [...new Set(panelTests)]; // Remove duplicates
      });
      
      setSelectedTests(initialSelectedTests);
    }
  }, [testCategories, testPanels, testDefinitions, selectedTests]);

  const handleQuickDateRange = (range) => {
    setQuickDateRange(range);
    const today = new Date();
    
    switch (range) {
      case '1week':
        setDateRange({ startDate: subDays(today, 7), endDate: today });
        break;
      case '1month':
        setDateRange({ startDate: subMonths(today, 1), endDate: today });
        break;
      case '3months':
        setDateRange({ startDate: subMonths(today, 3), endDate: today });
        break;
      case '6months':
        setDateRange({ startDate: subMonths(today, 6), endDate: today });
        break;
      case '1year':
        setDateRange({ startDate: subMonths(today, 12), endDate: today });
        break;
      default:
        setDateRange({ startDate: subMonths(today, 6), endDate: today });
        break;
    }
  };

  const handleCategoryToggle = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
    
    // Handle regular categories
    if (testCategories[categoryId]) {
      setSelectedTests(prev => ({
        ...prev,
        [categoryId]: [...testCategories[categoryId].tests]
      }));
    }
    
    // Handle test panels
    if (categoryId.startsWith('panel_')) {
      const panelId = categoryId.replace('panel_', '');
      const panel = testPanels.find(p => p.id === panelId);
      if (panel) {
        const panelTests = [];
        if (panel.analytes && Array.isArray(panel.analytes)) {
          // Direct analytes array (preferred method)
          panelTests.push(...panel.analytes);
        } else if (panel.tests && Array.isArray(panel.tests)) {
          // Test definitions references
          panel.tests.forEach(test => {
            if (typeof test === 'string') {
              const testDef = testDefinitions.find(def => def.id === test);
              if (testDef && testDef.analytes) {
                panelTests.push(...testDef.analytes);
              }
            } else if (test.analytes) {
              panelTests.push(...test.analytes);
            }
          });
        }
        setSelectedTests(prev => ({
          ...prev,
          [categoryId]: [...new Set(panelTests)]
        }));
      }
    }
  };

  const handleTestToggle = (categoryId, testName) => {
    setSelectedTests(prev => {
      const currentTests = prev[categoryId] || [];
      const isSelected = currentTests.includes(testName);
      
      return {
        ...prev,
        [categoryId]: isSelected 
          ? currentTests.filter(test => test !== testName)
          : [...currentTests, testName]
      };
    });
  };

  const handleSelectAllTests = (categoryId) => {
    // Handle regular categories
    if (testCategories[categoryId]) {
      setSelectedTests(prev => ({
        ...prev,
        [categoryId]: [...testCategories[categoryId].tests]
      }));
    }
    
    // Handle test panels
    if (categoryId.startsWith('panel_')) {
      const panelId = categoryId.replace('panel_', '');
      const panel = testPanels.find(p => p.id === panelId);
      if (panel) {
        const panelTests = [];
        if (panel.analytes && Array.isArray(panel.analytes)) {
          // Direct analytes array (preferred method)
          panelTests.push(...panel.analytes);
        } else if (panel.tests && Array.isArray(panel.tests)) {
          // Test definitions references
          panel.tests.forEach(test => {
            if (typeof test === 'string') {
              const testDef = testDefinitions.find(def => def.id === test);
              if (testDef && testDef.analytes) {
                panelTests.push(...testDef.analytes);
              }
            } else if (test.analytes) {
              panelTests.push(...test.analytes);
            }
          });
        }
        setSelectedTests(prev => ({
          ...prev,
          [categoryId]: [...new Set(panelTests)]
        }));
      }
    }
  };

  const handleDeselectAllTests = (categoryId) => {
    setSelectedTests(prev => ({
      ...prev,
      [categoryId]: []
    }));
  };

  // Process lab results for display
  const processedResults = useMemo(() => {
    if (!labResults.length) return { dates: [], tests: [], dataMatrix: {} };

    // Handle regular categories
    const category = testCategories[selectedCategory];
    // Handle test panels
    const isPanel = selectedCategory && selectedCategory.startsWith('panel_');
    const panel = isPanel ? testPanels.find(p => `panel_${p.id}` === selectedCategory) : null;
    
    if (!category && !panel) return { dates: [], tests: [], dataMatrix: {} };

    // Get selected tests for the current category or panel
    const selectedCategoryTests = selectedTests[selectedCategory] || [];
    if (selectedCategoryTests.length === 0) return { dates: [], tests: [], dataMatrix: {} };

    // Filter results for selected category/panel and selected tests only
    const categoryResults = labResults.filter(result => 
      selectedCategoryTests.includes(result.test_code)
    );

    // Get unique dates and tests (filter out invalid dates)
    const dates = [...new Set(categoryResults
      .map(r => safeFormatDate(r.collection_date, 'MM/dd/yyyy'))
      .filter(Boolean)
    )].sort((a, b) => new Date(b) - new Date(a));

    const tests = selectedCategoryTests.filter(test => 
      categoryResults.some(r => r.test_code === test)
    );

    // Create data matrix
    const dataMatrix = {};
    tests.forEach(test => {
      dataMatrix[test] = {};
      dates.forEach(date => {
        const result = categoryResults.find(r => 
          r.test_code === test && 
          safeFormatDate(r.collection_date, 'MM/dd/yyyy') === date
        );
        dataMatrix[test][date] = result || null;
      });
    });

    return { dates, tests, dataMatrix };
  }, [labResults, selectedCategory, testCategories, testPanels, selectedTests]);

  const renderCategoryNavigator = () => (
    <Paper sx={{ height: '100%', overflow: 'auto' }}>
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
          Test Categories
        </Typography>
        <Divider />
      </Box>
      
      <List dense>
        {Object.keys(testCategories).length === 0 && testPanels.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {loading ? 'Loading test categories...' : 'No test categories available'}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Regular test categories */}
            {Object.entries(testCategories).map(([categoryId, category]) => {
              const categoryTests = selectedTests[categoryId] || [];
              const allSelected = categoryTests.length === category.tests.length;
              
              return (
                <Accordion
                  key={categoryId}
                  expanded={expandedCategories.includes(categoryId)}
                  onChange={() => handleCategoryToggle(categoryId)}
                  sx={{ boxShadow: 0, '&:before': { display: 'none' } }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      minHeight: 40,
                      '&.Mui-expanded': { minHeight: 40 },
                      backgroundColor: selectedCategory === categoryId ? 'primary.lighter' : 'inherit'
                    }}
                  >
                    <Box 
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent accordion toggle
                        handleCategoryClick(categoryId);
                      }}
                    >
                      {category.icon}
                      <Typography variant="body2" sx={{ fontWeight: 500, flexGrow: 1 }}>
                        {category.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({categoryTests.length}/{category.tests.length})
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ py: 0 }}>
                    <List dense disablePadding>
                      {/* Select/Deselect All controls */}
                      <Box sx={{ pl: 2, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
                        <ButtonGroup size="small" variant="text">
                          <Button 
                            onClick={() => handleSelectAllTests(categoryId)}
                            disabled={allSelected}
                            sx={{ fontSize: '0.7rem', px: 1 }}
                          >
                            Select All
                          </Button>
                          <Button 
                            onClick={() => handleDeselectAllTests(categoryId)}
                            disabled={categoryTests.length === 0}
                            sx={{ fontSize: '0.7rem', px: 1 }}
                          >
                            Deselect All
                          </Button>
                        </ButtonGroup>
                      </Box>
                      
                      {/* Individual test checkboxes */}
                      {category.tests.map((testName) => (
                        <ListItem key={testName} dense disablePadding>
                          <ListItemButton
                            onClick={() => handleTestToggle(categoryId, testName)}
                            sx={{ pl: 2, py: 0.1 }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={categoryTests.includes(testName)}
                                  onChange={() => handleTestToggle(categoryId, testName)}
                                  size="small"
                                  sx={{ p: 0.25 }}
                                />
                              }
                              label={
                                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                  {testName}
                                </Typography>
                              }
                              sx={{ m: 0, width: '100%' }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              );
            })}

            {/* Test Panels - shown at the bottom */}
            {testPanels.length > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" sx={{ px: 2, py: 1, fontWeight: 600, color: 'text.secondary' }}>
                  Test Panels
                </Typography>
                {testPanels.map((panel) => {
                  const panelId = `panel_${panel.id}`;
                  const panelTests = selectedTests[panelId] || [];
                  
                  // Get all available tests for this panel
                  const availableTests = [];
                  if (panel.analytes && Array.isArray(panel.analytes)) {
                    // Direct analytes array (preferred method)
                    availableTests.push(...panel.analytes);
                  } else if (panel.tests && Array.isArray(panel.tests)) {
                    // Test definitions references
                    panel.tests.forEach(test => {
                      if (typeof test === 'string') {
                        const testDef = testDefinitions.find(def => def.id === test);
                        if (testDef && testDef.analytes) {
                          availableTests.push(...testDef.analytes);
                        }
                      } else if (test.analytes) {
                        availableTests.push(...test.analytes);
                      }
                    });
                  }
                  const uniqueAvailableTests = [...new Set(availableTests)];
                  
                  const allSelected = panelTests.length === uniqueAvailableTests.length;
                  
                  return (
                    <Accordion
                      key={panelId}
                      expanded={expandedCategories.includes(panelId)}
                      onChange={() => handleCategoryToggle(panelId)}
                      sx={{ boxShadow: 0, '&:before': { display: 'none' } }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          minHeight: 40,
                          '&.Mui-expanded': { minHeight: 40 },
                          backgroundColor: selectedCategory === panelId ? 'primary.lighter' : 'inherit'
                        }}
                      >
                        <Box 
                          sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent accordion toggle
                            handleCategoryClick(panelId);
                          }}
                        >
                          <AssessmentIcon />
                          <Typography variant="body2" sx={{ fontWeight: 500, flexGrow: 1 }}>
                            {panel.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ({panelTests.length}/{uniqueAvailableTests.length})
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ py: 0 }}>
                        <List dense disablePadding>
                          {/* Panel description */}
                          {panel.description && (
                            <Box sx={{ pl: 2, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
                              <Typography variant="caption" color="text.secondary">
                                {panel.description}
                              </Typography>
                            </Box>
                          )}
                          
                          {/* Select/Deselect All controls */}
                          <Box sx={{ pl: 2, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
                            <ButtonGroup size="small" variant="text">
                              <Button 
                                onClick={() => handleSelectAllTests(panelId)}
                                disabled={allSelected}
                                sx={{ fontSize: '0.7rem', px: 1 }}
                              >
                                Select All
                              </Button>
                              <Button 
                                onClick={() => handleDeselectAllTests(panelId)}
                                disabled={panelTests.length === 0}
                                sx={{ fontSize: '0.7rem', px: 1 }}
                              >
                                Deselect All
                              </Button>
                            </ButtonGroup>
                          </Box>
                          
                          {/* Individual test checkboxes */}
                          {uniqueAvailableTests.map((testName) => (
                            <ListItem key={testName} dense disablePadding>
                              <ListItemButton
                                onClick={() => handleTestToggle(panelId, testName)}
                                sx={{ pl: 2, py: 0.1 }}
                              >
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={panelTests.includes(testName)}
                                      onChange={() => handleTestToggle(panelId, testName)}
                                      size="small"
                                      sx={{ p: 0.25 }}
                                    />
                                  }
                                  label={
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                      {testName}
                                    </Typography>
                                  }
                                  sx={{ m: 0, width: '100%' }}
                                />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </>
            )}
          </>
        )}
      </List>
    </Paper>
  );

  const renderDateRangeControls = () => (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ pb: '16px !important' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
              Quick Date Ranges
            </Typography>
            <ButtonGroup size="small" variant="outlined" fullWidth>
              {[
                { key: '1week', label: '1W' },
                { key: '1month', label: '1M' },
                { key: '3months', label: '3M' },
                { key: '6months', label: '6M' },
                { key: '1year', label: '1Y' }
              ].map(({ key, label }) => (
                <Button
                  key={key}
                  variant={quickDateRange === key ? 'contained' : 'outlined'}
                  onClick={() => handleQuickDateRange(key)}
                  size="small"
                >
                  {label}
                </Button>
              ))}
            </ButtonGroup>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
              Custom Date Range
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={dateRange.startDate}
                  onChange={(date) => setDateRange(prev => ({ ...prev, startDate: date }))}
                  slotProps={{
                    textField: { size: 'small' }
                  }}
                />
                <DatePicker
                  label="End Date"
                  value={dateRange.endDate}
                  onChange={(date) => setDateRange(prev => ({ ...prev, endDate: date }))}
                  slotProps={{
                    textField: { size: 'small' }
                  }}
                />
              </LocalizationProvider>
            </Stack>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
              View Mode
            </Typography>
            <ButtonGroup size="small" variant="outlined" fullWidth>
              <Button
                variant={viewMode === VIEW_MODES.TEST_BY_DATE ? 'contained' : 'outlined'}
                onClick={() => setViewMode(VIEW_MODES.TEST_BY_DATE)}
                startIcon={<ViewListIcon />}
              >
                Tests by Dates
              </Button>
              <Button
                variant={viewMode === VIEW_MODES.DATE_BY_TEST ? 'contained' : 'outlined'}
                onClick={() => setViewMode(VIEW_MODES.DATE_BY_TEST)}
                startIcon={<ViewColumnIcon />}
              >
                Dates by Tests
              </Button>
            </ButtonGroup>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderResultsTable = () => {
    const { dates, tests, dataMatrix } = processedResults;
    
    if (!dates.length || !tests.length) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          No lab results found for the selected category and date range.
        </Alert>
      );
    }

    const getResultDisplay = (result) => {
      if (!result) return { value: '-', color: 'inherit', chip: null };
      
      let color = 'inherit';
      let chip = null;
      
      if (result.abnormal && result.abnormal !== 'N') {
        color = result.abnormal === 'H' || result.abnormal === 'HH' ? 'error.main' : 
               result.abnormal === 'L' || result.abnormal === 'LL' ? 'warning.main' : 
               'error.main';
        chip = (
          <Chip 
            label={result.abnormal} 
            size="small" 
            color={result.abnormal.includes('H') ? 'error' : 'warning'}
            sx={{ ml: 0.5, height: 16, fontSize: '0.65rem' }}
          />
        );
      }
      
      return { 
        value: result.result, 
        color, 
        chip,
        units: result.units,
        refRange: result.ref_range
      };
    };

    if (viewMode === VIEW_MODES.TEST_BY_DATE) {
      // Tests as rows, dates as columns
      return (
        <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'grey.50', minWidth: 150 }}>
                  Test
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'grey.50', minWidth: 100 }}>
                  Units
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'grey.50', minWidth: 120 }}>
                  Reference Range
                </TableCell>
                {dates.map(date => (
                  <TableCell 
                    key={date}
                    sx={{ 
                      fontWeight: 600, 
                      backgroundColor: 'grey.50',
                      minWidth: 100,
                      textAlign: 'center'
                    }}
                  >
                    {date}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tests.map(test => {
                const firstResult = dates.map(date => dataMatrix[test][date]).find(r => r);
                return (
                  <TableRow key={test}>
                    <TableCell sx={{ fontWeight: 500 }}>{test}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                      {firstResult?.units || ''}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                      {firstResult?.ref_range || ''}
                    </TableCell>
                    {dates.map(date => {
                      const result = dataMatrix[test][date];
                      const display = getResultDisplay(result);
                      return (
                        <TableCell 
                          key={date}
                          sx={{ 
                            textAlign: 'center',
                            color: display.color,
                            fontWeight: display.color !== 'inherit' ? 600 : 400
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {display.value}
                            {display.chip}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      );
    } else {
      // Dates as rows, tests as columns
      return (
        <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'grey.50', minWidth: 120 }}>
                  Date
                </TableCell>
                {tests.map(test => (
                  <TableCell 
                    key={test}
                    sx={{ 
                      fontWeight: 600, 
                      backgroundColor: 'grey.50',
                      minWidth: 80,
                      textAlign: 'center'
                    }}
                  >
                    {test}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {dates.map(date => (
                <TableRow key={date}>
                  <TableCell sx={{ fontWeight: 500 }}>{date}</TableCell>
                  {tests.map(test => {
                    const result = dataMatrix[test][date];
                    const display = getResultDisplay(result);
                    return (
                      <TableCell 
                        key={test}
                        sx={{ 
                          textAlign: 'center',
                          color: display.color,
                          fontWeight: display.color !== 'inherit' ? 600 : 400
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {display.value}
                          {display.chip}
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }
  };

  // Render main content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'lab_reports':
        return (
          <Grid container spacing={2} sx={{ flexGrow: 1 }}>
            {/* Category Navigator */}
            <Grid item xs={12} md={2}>
              {renderCategoryNavigator()}
            </Grid>

            {/* Results Display */}
            <Grid item xs={12} md={10}>
              <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                      {testCategories[selectedCategory]?.name || 'Lab Results'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Export Results">
                        <IconButton size="small">
                          <ExportIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Filter Results">
                        <IconButton size="small">
                          <FilterListIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Showing results from {format(dateRange.startDate, 'MM/dd/yyyy')} to {format(dateRange.endDate, 'MM/dd/yyyy')}
                  </Typography>
                </Box>
                
                <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                  {renderResultsTable()}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        );
      
      case 'radiology_reports':
        return (
          <Box sx={{ flexGrow: 1 }}>
            {/* Date Range Controls */}
            {renderDateRangeControls()}
            
            {/* Radiology Reports List */}
            <Paper 
              elevation={0}
              sx={{ 
                mt: 2, 
                height: 'calc(100vh - 300px)', 
                overflow: 'auto',
                border: 1,
                borderColor: 'divider',
                borderRadius: 2
              }}
            >
              {/* Header */}
              <Box 
                sx={{ 
                  p: 2, 
                  borderBottom: 1, 
                  borderColor: 'divider',
                  bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RadiologyIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Radiology Reports
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Export Reports">
                      <IconButton 
                        size="small"
                        sx={{ 
                          color: 'text.secondary',
                          '&:hover': { color: 'primary.main' }
                        }}
                      >
                        <ExportIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Filter Reports">
                      <IconButton 
                        size="small"
                        sx={{ 
                          color: 'text.secondary',
                          '&:hover': { color: 'primary.main' }
                        }}
                      >
                        <FilterListIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Showing reports from {format(dateRange.startDate, 'MM/dd/yyyy')} to {format(dateRange.endDate, 'MM/dd/yyyy')}
                </Typography>
              </Box>
              
              {/* Reports List */}
              <Box sx={{ p: 2 }}>
                {radiologyReports.length === 0 ? (
                  <Paper 
                    variant="outlined"
                    sx={{ 
                      textAlign: 'center', 
                      py: 6,
                      px: 3,
                      borderStyle: 'dashed',
                      borderColor: 'divider',
                      bgcolor: 'transparent'
                    }}
                  >
                    <RadiologyIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No Radiology Reports Found
                    </Typography>
                    <Typography variant="body2" color="text.disabled">
                      No radiology reports available for the selected date range.
                    </Typography>
                  </Paper>
                ) : (
                  <Stack spacing={2}>
                    {radiologyReports.map((report) => {
                      const isFinal = report.reportStatus === 'Final' || report.review_status === 'final';
                      return (
                        <Card 
                          key={report.id}
                          variant="outlined"
                          sx={{ 
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            borderColor: 'divider',
                            '&:hover': { 
                              borderColor: 'primary.main',
                              boxShadow: theme.shadows[4],
                              transform: 'translateY(-2px)'
                            }
                          }}
                          onClick={() => {
                            setSelectedRadiologyReport(report);
                            setShowRadiologyModal(true);
                          }}
                        >
                          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                            {/* Report Header */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                {report.testType || report.comments || 'Radiology Report'}
                              </Typography>
                              <Chip 
                                label={report.reportStatus || report.review_status || 'Unknown'} 
                                color={isFinal ? 'success' : 'warning'}
                                size="small"
                                sx={{ fontWeight: 500 }}
                              />
                            </Box>
                            
                            {/* Report Metadata */}
                            <Grid container spacing={2} sx={{ mb: 1.5 }}>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" component="div">
                                  Provider
                                </Typography>
                                <Typography variant="body2" color="text.primary">
                                  {report.provider_id || 'Unknown'}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" component="div">
                                  Date
                                </Typography>
                                <Typography variant="body2" color="text.primary">
                                  {safeFormatDate(report.date_report, 'MMM dd, yyyy') || 'Unknown Date'}
                                </Typography>
                              </Grid>
                            </Grid>
                            
                            {/* DICOM Images Badge */}
                            {report.images && report.images.length > 0 && (
                              <Box sx={{ mb: 1.5 }}>
                                <Chip 
                                  icon={<RadiologyIcon sx={{ fontSize: 16 }} />}
                                  label={`${report.images.length} DICOM image${report.images.length > 1 ? 's' : ''}`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ 
                                    borderRadius: 1,
                                    '& .MuiChip-icon': { ml: 0.5 }
                                  }}
                                />
                              </Box>
                            )}
                            
                            {/* Report Text Preview */}
                            {report.reportText && (
                              <Paper 
                                variant="outlined"
                                sx={{ 
                                  p: 1.5,
                                  bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                                  borderColor: 'divider'
                                }}
                              >
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary"
                                  sx={{ 
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    lineHeight: 1.5
                                  }}
                                >
                                  {report.reportText}
                                </Typography>
                              </Paper>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </Paper>
          </Box>
        );
      
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading personalized reports...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Patient Header */}
      {showPatientHeader && (patient || encounter) && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <AssessmentIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            </Grid>
            <Grid item xs>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {patient?.name || encounter?.patientName || 'Unknown Patient'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
                {patient?.mrn && (
                  <Chip label={`MRN: ${patient.mrn}`} size="small" />
                )}
                {encounter?.fin && (
                  <Chip label={`FIN: ${encounter.fin}`} size="small" />
                )}
                {patient?.dateOfBirth && safeFormatDate(patient.dateOfBirth, 'MM/dd/yyyy') && (
                  <Chip 
                    label={`DOB: ${safeFormatDate(patient.dateOfBirth, 'MM/dd/yyyy')}`} 
                    size="small" 
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Report Tabs */}
      {availableTabs.length > 0 && (
        <Paper sx={{ mb: 2 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            variant="scrollable" 
            scrollButtons="auto"
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              '& .MuiTab-root': {
                minHeight: 36,
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.02em',
                px: 1.5,
                py: 0.5,
                minWidth: 'auto',
                textTransform: 'none'
              },
              '& .MuiTabs-indicator': {
                height: 2
              },
              '& .MuiTabs-scrollButtons': {
                width: 32
              }
            }}
          >
            {availableTabs.map((tab) => (
              <Tab 
                key={tab.key}
                value={tab.key}
                label={tab.label}
              />
            ))}
          </Tabs>
        </Paper>
      )}

      {/* Date Range and View Controls - Only show for Lab tab */}
      {activeTab === 'lab_reports' && renderDateRangeControls()}

      {/* Main Content Area */}
      {renderTabContent()}

      {/* Radiology Report Details Modal */}
      {selectedRadiologyReport && (
        <ReportDetailsModal
          open={showRadiologyModal}
          onClose={() => {
            setShowRadiologyModal(false);
            setSelectedRadiologyReport(null);
          }}
          report={selectedRadiologyReport}
        />
      )}
    </Box>
  );
};

export default PersonalizedReporting; 